<!--
  KeyboardShortcutsModal.vue

  The "?" overlay. Reads the live shortcutRegistry (populated as views bind on
  mount and pruned as they unbind), so it lists exactly the shortcuts active
  wherever the user currently is.
-->
<template>
  <ModalContainer :show="show" @close="emit('close')">
    <div class="shortcuts-modal">
      <h2 class="text-4 mb-4">Keyboard shortcuts</h2>

      <p v-if="!groups.length" class="text-muted">No shortcuts available here.</p>

      <div v-else class="shortcuts-groups">
        <div v-for="g in groups" :key="g.name" class="shortcuts-group">
          <h3 class="shortcuts-group-title text-7 font-semibold">{{ g.name }}</h3>
          <ul class="shortcuts-list">
            <li v-for="s in g.shortcuts" :key="s.combo" class="shortcuts-row">
              <span class="shortcuts-keys">
                <template v-for="(step, i) in s.steps" :key="i">
                  <span v-if="i > 0" class="shortcuts-then">then</span>
                  <kbd v-for="(k, j) in step" :key="j" class="shortcuts-kbd">{{ k }}</kbd>
                </template>
              </span>
              <span class="shortcuts-desc">{{ s.label }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </ModalContainer>
</template>

<script setup lang="ts">
import { computed } from "vue"
import ModalContainer from "@auger/ui/components/ModalContainer.vue"
import { shortcutRegistry } from "@/composables/useMousetrap"

defineProps<{ show: boolean }>()
const emit = defineEmits<{ close: [] }>()

const KEY_LABELS: Record<string, string> = {
  command: "⌘",
  ctrl: "Ctrl",
  shift: "⇧",
  alt: "⌥",
  option: "⌥",
  enter: "↵",
  esc: "Esc",
  escape: "Esc",
  up: "↑",
  down: "↓",
  left: "←",
  right: "→",
  space: "Space",
  backspace: "⌫",
  tab: "Tab",
}

// A chord ("command+k") splits into its keys; a lone "+" is the literal plus
// key, not a separator. Sequences ("g i") are split on spaces upstream. Keys
// are shown verbatim — case is significant ("G" is shift+g, not "g").
function formatChord(chord: string): string[] {
  if (chord === "+") return ["+"]
  return chord.split("+").map((k) => KEY_LABELS[k] ?? k)
}

// Groups lead with Navigation, then Global, then everything else alphabetically.
const GROUP_ORDER = ["Navigation", "Global"]
function groupRank(name: string): number {
  const i = GROUP_ORDER.indexOf(name)
  return i === -1 ? GROUP_ORDER.length : i
}

// Group the live registry by `group`, ordering groups per GROUP_ORDER and
// shortcuts by label within each group.
const groups = computed(() => {
  const byGroup = new Map<string, { combo: string; label: string; steps: string[][] }[]>()
  for (const [combo, { label, group }] of shortcutRegistry.entries()) {
    const rows = byGroup.get(group) ?? []
    rows.push({ combo, label, steps: combo.split(" ").map(formatChord) })
    byGroup.set(group, rows)
  }
  return [...byGroup.entries()]
    .map(([name, shortcuts]) => ({
      name,
      shortcuts: shortcuts.sort((a, b) => a.label.localeCompare(b.label)),
    }))
    .sort((a, b) => groupRank(a.name) - groupRank(b.name) || a.name.localeCompare(b.name))
})
</script>

<style>
.shortcuts-modal {
  /* Explicit width so the modal-content shrink-wraps to this rather than to
     the columns' intrinsic width (~730px). Must exceed that intrinsic width
     to actually widen — a smaller value gets clamped away. */
  width: 900px;
  max-width: 100%;
}

/* showModal() focuses the <dialog> itself (no autofocus target inside), which
   paints the browser focus ring. Scoped to this modal via :has so other
   modals keep their default behaviour. */
.modal-mask:has(.shortcuts-modal) {
  outline: none;
}

.shortcuts-groups {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 1.25rem 2rem;
}

.shortcuts-group {
  flex: 1 1 240px;
  min-width: 220px;
}

.shortcuts-group-title {
  margin: 0 0 0.25rem;
  color: var(--auger-text-weak);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.shortcuts-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.shortcuts-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.4rem 0;

  & + & {
    border-top: 1px solid var(--auger-border-light);
  }
}

.shortcuts-keys {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  flex-shrink: 0;
}

.shortcuts-then {
  color: var(--auger-text-weak);
  font-size: var(--auger-size-7);
}

.shortcuts-kbd {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.4rem;
  padding: 0.1rem 0.4rem;
  font-family: inherit;
  font-size: var(--auger-size-7);
  line-height: 1.4;
  color: var(--auger-text);
  background: var(--auger-bg-muted);
  border: 1px solid var(--auger-border);
  border-radius: var(--auger-radius);
}

.shortcuts-desc {
  color: var(--auger-text);
  font-size: var(--auger-size-6);
  text-align: right;
}
</style>
